import { Button, Tooltip, Typography } from 'antd';
import { IconExternalLink, IconUsers } from '@tabler/icons-react';
import type { CSSProperties } from 'react';
import type { DeclarationCase } from '@/api/cases';
import { CASE_STATUS_LABEL, CASE_STATUS_VISUAL } from '@/api/cases';
import type { Role } from '@/lib/role';
import { CaseActionsMenu } from './CaseActionsMenu';

/**
 * 单张申报案卡片 — Kanban 列内 unit。
 *
 * 展示：
 *   - 顶部：subject_id 短码 + status chip
 *   - 主体：policy_pack_id 短码 + declare_year/batch_code
 *   - 元信息：gov_receipt_no / frozen_policy_pack_version（如有）
 *   - 底部：操作菜单（按 RBAC + 状态机过滤）
 *
 * 0 hex 硬编码 — 全部走 CSS var。
 */
export interface CaseCardProps {
  case_: DeclarationCase;
  role: Role | null;
  onFreeze: (c: DeclarationCase) => void;
  onSubmit: (c: DeclarationCase) => void;
  onStartReview: (c: DeclarationCase) => void;
  onRecordReview: (c: DeclarationCase) => void;
  onWithdraw: (c: DeclarationCase) => void;
  onDelete: (c: DeclarationCase) => void;
  /** M3-ρ-4：可选 — 未传则不渲染生成按钮（兼容旧测试） */
  onGenerateArtifacts?: (c: DeclarationCase) => void;
  /** S20-C0:打开指派/改派 Modal */
  onAssign?: (c: DeclarationCase) => void;
  /** S20-C1:打开案工作台(/cases/:id) */
  onView?: (c: DeclarationCase) => void;
  pending?: boolean;
}

function shortId(id: string): string {
  if (!id) return '—';
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return iso.slice(0, 10);
}

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-tertiary)',
};

const valueStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-mono)',
};

export function CaseCard({
  case_,
  role,
  onFreeze,
  onSubmit,
  onStartReview,
  onRecordReview,
  onWithdraw,
  onDelete,
  onGenerateArtifacts,
  onAssign,
  onView,
  pending,
}: CaseCardProps) {
  const visual = CASE_STATUS_VISUAL[case_.status];
  const ownerName = case_.owner_name?.trim() || null;
  const hasOwner = !!case_.owner_account_id;
  return (
    <div
      style={{
        background: 'var(--color-bg-card)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-sm)',
        borderLeft: `3px solid ${visual.fg}`,
        padding: 'var(--space-3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <Tooltip title={`企业 ID: ${case_.subject_id}`}>
          <Typography.Text
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)' as CSSProperties['fontWeight'],
              color: 'var(--color-text-primary)',
              // Sprint19-fix:有 subject_name 显示名称(普通字体),无则 fallback shortId(mono)
              fontFamily: case_.subject_name
                ? 'inherit'
                : 'var(--font-family-mono)',
            }}
          >
            {case_.subject_name || shortId(case_.subject_id)}
          </Typography.Text>
        </Tooltip>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            background: visual.bg,
            color: visual.fg,
            border: `1px solid ${visual.border}`,
            borderRadius: 'var(--radius-pill)',
            padding: '0 8px',
            fontSize: 'var(--font-size-xs)',
            lineHeight: '18px',
            whiteSpace: 'nowrap',
          }}
        >
          {CASE_STATUS_LABEL[case_.status]}
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '64px 1fr',
          rowGap: 4,
          columnGap: 8,
        }}
      >
        <span style={labelStyle}>负责人</span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {hasOwner ? (
            <Tooltip title={ownerName ?? '已指派'}>
              <span
                style={{
                  color: 'var(--color-text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                {ownerName ?? '已指派'}
              </span>
            </Tooltip>
          ) : (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: 'var(--color-bg-elevated)',
                color: 'var(--color-text-tertiary)',
                border: '1px solid var(--color-border-soft)',
                borderRadius: 'var(--radius-pill)',
                padding: '0 8px',
                fontSize: 'var(--font-size-xs)',
                lineHeight: '18px',
              }}
            >
              未指派
            </span>
          )}
        </span>

        <span style={labelStyle}>政策包</span>
        <Tooltip title={`政策包 ID: ${case_.policy_pack_id}`}>
          <span
            style={{
              ...valueStyle,
              // Sprint19-fix:有 policy_pack_name 用普通字体,无则 mono shortId
              fontFamily: case_.policy_pack_name
                ? 'inherit'
                : 'var(--font-family-mono)',
            }}
          >
            {case_.policy_pack_name || shortId(case_.policy_pack_id)}
          </span>
        </Tooltip>

        <span style={labelStyle}>年度/批次</span>
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {case_.declare_year ?? '—'}
          {case_.batch_code ? ` · ${case_.batch_code}` : ''}
        </span>

        {case_.frozen_policy_pack_version && (
          <>
            <span style={labelStyle}>包版本</span>
            <span style={valueStyle}>{case_.frozen_policy_pack_version}</span>
          </>
        )}

        {case_.gov_receipt_no && (
          <>
            <span style={labelStyle}>受理号</span>
            <span style={valueStyle}>{case_.gov_receipt_no}</span>
          </>
        )}

        {case_.review_result && (
          <>
            <span style={labelStyle}>审查</span>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                color:
                  case_.review_result === 'approved'
                    ? 'var(--color-success)'
                    : 'var(--color-danger-2)',
              }}
            >
              {case_.review_result}
            </span>
          </>
        )}
      </div>

      <div
        style={{
          borderTop: '1px dashed var(--color-border-soft)',
          paddingTop: 6,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <Tooltip
          title={`创建: ${fmtDate(case_.created_at)} · 更新: ${fmtDate(case_.updated_at)}`}
        >
          <span style={labelStyle}>更新 {fmtDate(case_.updated_at)}</span>
        </Tooltip>
        <span style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
          {onView && (
            <Tooltip title="打开工作台 — 指标 / 材料 / 时间轴">
              <Button
                size="small"
                icon={<IconExternalLink size={12} />}
                onClick={() => onView(case_)}
                style={{ fontSize: 'var(--font-size-xs)', height: 26, padding: '0 6px' }}
              >
                工作台
              </Button>
            </Tooltip>
          )}
          {onAssign && (
            <Tooltip title="指派 / 改派">
              <Button
                size="small"
                icon={<IconUsers size={12} />}
                onClick={() => onAssign(case_)}
                style={{ fontSize: 'var(--font-size-xs)', height: 26, padding: '0 6px' }}
              >
                指派
              </Button>
            </Tooltip>
          )}
          <CaseActionsMenu
          case_={case_}
          role={role}
          onFreeze={() => onFreeze(case_)}
          onSubmit={() => onSubmit(case_)}
          onStartReview={() => onStartReview(case_)}
          onRecordReview={() => onRecordReview(case_)}
          onWithdraw={() => onWithdraw(case_)}
          onDelete={() => onDelete(case_)}
          onGenerateArtifacts={
            onGenerateArtifacts ? () => onGenerateArtifacts(case_) : undefined
          }
          pending={pending}
        />
        </span>
      </div>
    </div>
  );
}
